import { CREATURE_TIMING, type CreatureState } from "@eldrun/content";

/** O que a máquina sabe de cada clipe (do módulo gerado): duração, laço, a velocidade dos pés e o impacto. */
export interface CreatureClipInfo {
  readonly duration: number;
  readonly loop: boolean;
  /** Locomoção: m/s em que os pés não escorregam com o clipe a 1× (na altura do modelo). */
  readonly speed?: number;
  readonly impact?: number;
}

export type CreatureClipTable = Readonly<Record<string, CreatureClipInfo>>;

/** O que a animação lê da criatura (interpolada) e do golpe em andamento, a cada quadro. */
export interface CreatureSignals {
  state: CreatureState;
  /** Segundos desde que entrou no estado. */
  stateTime: number;
  /** Velocidade horizontal estimada (m/s). */
  speed: number;
  /** Voadora no ar. */
  flying: boolean;
  /**
   * O golpe do quadro (`strikeTime`): o clipe e o instante. `free`: toca também fora do preparo, do golpe
   * e da recuperação (o cuspe do Dragão planando), menos ao surgir, cambalear, no pouso vulnerável e na morte.
   */
  strike?: { clip: string; time: number; free?: boolean };
}

/** Um clipe na pose: instante e peso (os pesos somam 1). */
export interface CreatureSample {
  clip: string;
  time: number;
  weight: number;
}

/** A pose do quadro: os clipes e o que o corpo faz por cima deles. */
export interface CreatureMix {
  samples: CreatureSample[];
  /** Deslocamento do corpo em y (m): sobe do chão ao surgir. */
  lift: number;
  /** Giro em torno do eixo da frente (rad): o balanço do cambalear e o tombo da ave morta. */
  roll: number;
  /** Inclinação para a frente (rad): o pouso vulnerável. */
  pitch: number;
  /** Dissolve (0 inteiro, 1 sumido): a morte, o amanhecer e o sumiço da Rainha-Bruxa. */
  dissolve: number;
  /** O clipe de maior peso (`stats()`). */
  clip: string;
}

/** Transição entre poses (s): o golpe entra rápido e sai devagar. */
const FADE = 0.15;
const STRIKE_IN = 0.08;
const STRIKE_OUT = 0.2;
/** Abaixo disto (m/s), parada. */
const IDLE_SPEED = 0.15;
/** Teto da velocidade da locomoção (acima, os pés escorregam, mas a perna não vira hélice). */
const MAX_RATE = 2.6;
/**
 * A morte some de 0,9 s até 1,4 s, antes de o servidor tirar o corpo (`CREATURE_TIMING.corpse`, 1,5 s):
 * a saída do schema chega com o patch, que pode adiantar uns 50 ms, e não sobram pedaços a sumir de vez.
 */
export const DEATH_DISSOLVE = { from: 0.9, to: CREATURE_TIMING.corpse - 0.1 } as const;
/** O amanhecer leva 1,2 s; o sumiço da Rainha, 0,4 s; a volta dela, 0,2 s. */
const DAWN = 1.2;
const VANISH = 0.4;
const RETURN = 0.2;
/** Voadoras: abaixo disto paira batendo as asas; acima de `fast`, bate mais forte; entre, plana. */
const FLY = { glide: 2, fast: 6 } as const;
/** Instante antes do fim para segurar a última pose dos clipes que não repetem. */
const END = 1e-3;

/** Estados em que o golpe vale. */
const ATTACKING: ReadonlySet<CreatureState> = new Set(["preparando", "golpeando", "recuperando"]);
/** Onde nem o golpe solto (`free`) toca: o corpo faz outra coisa. */
const BLOCKS_FREE: ReadonlySet<CreatureState> = new Set(["surgindo", "cambaleando", "vulneravel", "morta", "dissolvendo", "sumindo", "enrolado", "cavando"]);

const fraction = (t: number, d: number) => (d > 0 ? t - Math.floor(t / d) * d : 0);
const clampTime = (t: number, d: number) => Math.max(0, Math.min(t, d - END));
const smooth = (k: number) => k * k * (3 - 2 * k);

type Kind = "locomocao" | "golpe" | "morte";

/** Junta amostras do mesmo clipe (somando o peso e ficando com o instante da mais pesada). */
function merge(samples: readonly CreatureSample[]): CreatureSample[] {
  const out: CreatureSample[] = [];
  for (const s of samples) {
    if (s.weight <= 0) continue;
    const twin = out.find((o) => o.clip === s.clip);
    if (!twin) out.push({ ...s });
    else {
      if (s.weight > twin.weight) twin.time = s.time;
      twin.weight += s.weight;
    }
  }
  return out;
}

/**
 * Máquina de estados da criatura animada (F50), pura: do estado do servidor, da velocidade e do golpe
 * em andamento, a pose do quadro como amostras de clipes, sem tocar no three. A locomoção mistura
 * parado, andando e correndo pela velocidade, com a fase dos pés comum aos dois e a velocidade em que
 * eles não escorregam (como a dos avatares); a voadora paira, plana e bate as asas. O golpe vem do
 * `creature-strike` (o impacto no acerto do servidor). Por cima: surgir sobe o corpo do chão em 1,2 s,
 * cambalear balança, o pouso vulnerável inclina, morrer toca a morte e segura o último quadro antes de
 * se dissolver, e o amanhecer e o sumiço da Rainha dissolvem. Sem `dano` e `surgindo` nos arquivos
 * (F50), o cambalear e o surgir são do corpo.
 */
export class CreatureStateMachine {
  private readonly clips: CreatureClipTable;
  private readonly height: number;
  private time = 0;
  private idle = 0;
  /** Fase dos pés (0 a 1) da locomoção: a troca com o boneco procedural a copia. */
  phase = 0;
  private kind: Kind = "locomocao";
  private from: CreatureSample[] = [];
  private fadeAt = 0;
  private fadeFor = FADE;
  private last: CreatureSample[] = [];
  /** Quando a Rainha voltou do sumiço (s, no relógio da máquina). */
  private returnedAt = -Infinity;
  private previous: CreatureState | undefined;

  /** `tumble`: a morte é uma pose segurada (a ave) e o corpo tomba por cima, como o boneco procedural. */
  private readonly tumble: boolean;

  constructor(clips: CreatureClipTable, height: number, tumble = false) {
    this.clips = clips;
    this.height = height;
    this.tumble = tumble;
  }

  step(s: CreatureSignals, dt: number): CreatureMix {
    this.time += dt;
    this.idle += dt;
    if (this.previous === "sumindo" && s.state !== "sumindo") this.returnedAt = this.time;
    this.previous = s.state;
    this.advancePhase(s, dt);

    const kind: Kind = s.state === "morta" && this.clips["morte"] ? "morte" : s.strike && this.clips[s.strike.clip] && (ATTACKING.has(s.state) || (s.strike.free === true && !BLOCKS_FREE.has(s.state))) ? "golpe" : "locomocao";
    if (kind !== this.kind) {
      this.from = this.last;
      this.fadeAt = this.time;
      this.fadeFor = kind === "golpe" ? STRIKE_IN : this.kind === "golpe" ? STRIKE_OUT : FADE;
      this.kind = kind;
    }
    const current = kind === "morte" ? [{ clip: "morte", time: clampTime(s.stateTime, this.clips["morte"]!.duration), weight: 1 }] : kind === "golpe" ? [{ clip: s.strike!.clip, time: clampTime(s.strike!.time, this.clips[s.strike!.clip]!.duration), weight: 1 }] : this.locomotion(s);
    const k = Math.min(1, (this.time - this.fadeAt) / this.fadeFor);
    const samples = k >= 1 ? current : merge([...current.map((x) => ({ ...x, weight: x.weight * k })), ...this.from.map((x) => ({ ...x, weight: x.weight * (1 - k) }))]);
    this.last = samples;
    const t = s.stateTime;
    const top = samples.reduce<CreatureSample | undefined>((best, x) => (best && best.weight >= x.weight ? best : x), undefined);
    return {
      samples,
      lift: s.state === "surgindo" ? -0.75 * this.height * (1 - smooth(Math.min(1, t / CREATURE_TIMING.emerge))) : 0,
      roll: s.state === "cambaleando" ? Math.sin(t * 22) * 0.3 * Math.max(0, 1 - t / CREATURE_TIMING.stagger) : s.state === "morta" && (this.tumble || !this.clips["morte"]) ? Math.min(1.5, t * 4) : 0,
      pitch: s.state === "vulneravel" ? 0.35 : 0,
      dissolve: this.dissolve(s),
      clip: top?.clip ?? "parado",
    };
  }

  /** Quanto o corpo já se desfez (0 a 1) pelo estado. */
  private dissolve(s: CreatureSignals): number {
    const t = s.stateTime;
    switch (s.state) {
      case "morta":
        return Math.max(0, Math.min(1, (t - DEATH_DISSOLVE.from) / (DEATH_DISSOLVE.to - DEATH_DISSOLVE.from)));
      case "dissolvendo":
        return Math.min(1, t / DAWN);
      case "sumindo":
        return Math.min(1, t / VANISH);
      default: {
        // De volta do sumiço: reaparece pelo dissolve (sem resto de float: 0 volta ao material inteiro).
        const back = 1 - (this.time - this.returnedAt) / RETURN;
        return back > 1e-6 ? back : 0;
      }
    }
  }

  private advancePhase(s: CreatureSignals, dt: number): void {
    const walk = this.clips["andando"];
    const run = this.clips["correndo"] ?? walk;
    if (!walk || !run) return;
    if (s.flying || walk.speed === undefined || run.speed === undefined) {
      this.phase = fraction(this.phase + dt / walk.duration, 1);
      return;
    }
    // Ciclos por segundo: a passada do clipe na velocidade dele; entre andar e correr, a média.
    const toRun = run.speed > walk.speed ? Math.max(0, Math.min(1, (s.speed - walk.speed) / (run.speed - walk.speed))) : 0;
    const strideWalk = walk.speed * walk.duration;
    const strideRun = run.speed * run.duration;
    const cycles = s.speed < walk.speed ? 1 / walk.duration : Math.min(MAX_RATE / run.duration, s.speed / (strideWalk + (strideRun - strideWalk) * toRun));
    this.phase = fraction(this.phase + dt * cycles, 1);
  }

  private locomotion(s: CreatureSignals): CreatureSample[] {
    const c = this.clips;
    const idle = c["parado"]!;
    const walk = c["andando"] ?? idle;
    const run = c["correndo"] ?? walk;
    const out: CreatureSample[] = [];
    const add = (clip: string, time: number, w: number) => w > 0 && c[clip] && out.push({ clip, time, weight: w });
    // Pouso vulnerável e cambalear: parado devagar (o corpo faz o resto).
    if (s.state === "vulneravel" || s.state === "cambaleando" || s.state === "surgindo") {
      add("parado", fraction(this.idle * (s.state === "vulneravel" ? 0.5 : 1), idle.duration), 1);
      return out;
    }
    if (s.flying) {
      if (s.speed < FLY.glide) add("parado", fraction(this.idle, idle.duration), 1);
      else if (s.speed < FLY.fast) {
        const k = Math.min(1, (s.speed - FLY.glide) / 1);
        add("parado", fraction(this.idle, idle.duration), 1 - k);
        add("andando", this.phase * walk.duration, k);
      } else add("correndo", fraction(this.idle * 1.3, run.duration), 1);
      return merge(out);
    }
    const walkSpeed = walk.speed ?? 1;
    const runSpeed = run.speed ?? 3;
    if (s.speed < IDLE_SPEED) add("parado", fraction(this.idle, idle.duration), 1);
    else if (s.speed < walkSpeed) {
      const k = Math.min(1, (s.speed - IDLE_SPEED) / Math.max(1e-3, walkSpeed - IDLE_SPEED));
      add("parado", fraction(this.idle, idle.duration), 1 - k);
      add("andando", this.phase * walk.duration, k);
    } else {
      const k = runSpeed > walkSpeed ? Math.min(1, (s.speed - walkSpeed) / (runSpeed - walkSpeed)) : 0;
      add("andando", this.phase * walk.duration, 1 - k);
      add("correndo", this.phase * run.duration, k);
    }
    return merge(out);
  }
}
